import { useRef, useState } from "react";
import { Reveal, SectionHeading } from "./Reveal";

function BeforeAfterCard({ item, index }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const move = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  };

  return (
    <div>
      <div
        ref={ref}
        data-testid={`ba-slider-${index}`}
        role="slider"
        tabIndex={0}
        aria-label={`Compare before and after: ${item.label || `groom ${index + 1}`}`}
        aria-valuemin={4}
        aria-valuemax={96}
        aria-valuenow={Math.round(pos)}
        onKeyDown={(e) => {
          if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
          e.preventDefault();
          setPos((value) => e.key === "Home" ? 4 : e.key === "End" ? 96 : Math.max(4, Math.min(96, value + (e.key === "ArrowRight" ? 5 : -5))));
        }}
        className="relative aspect-[4/3] cursor-ew-resize touch-pan-y select-none overflow-hidden rounded-lg shadow-soft"
        onPointerDown={(e) => { dragging.current = true; e.currentTarget.setPointerCapture(e.pointerId); move(e.clientX); }}
        onPointerMove={(e) => dragging.current && move(e.clientX)}
        onPointerUp={() => { dragging.current = false; }}
        onPointerCancel={() => { dragging.current = false; }}
      >
        <img src={item.afterUrl} alt={`${item.label} — after grooming`} draggable={false} className="absolute inset-0 h-full w-full object-cover object-center" />
        <img
          src={item.beforeUrl}
          alt={`${item.label} — before grooming`}
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover object-center"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        />
        <span className="absolute left-4 top-4 rounded-md bg-lavender-700 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-white shadow-soft">Before</span>
        <span className="absolute right-4 top-4 rounded-md bg-lavender-700 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-white shadow-soft">After</span>
        <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
          <div className="absolute inset-y-0 -ml-px w-0.5 bg-white/90 shadow" />
          <div
            data-testid={`ba-handle-${index}`}
            className="absolute top-1/2 -ml-6 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-md border-4 border-white bg-lavender-700 text-white shadow-lift"
          >
            <span className="h-4 w-1 border-x border-white" aria-hidden="true" />
          </div>
        </div>
      </div>
      {item.label && <p data-testid={`ba-caption-${index}`} className="mt-4 text-base font-medium text-stone-700">{item.label}</p>}
    </div>
  );
}

export default function Transformations({ transformations }) {
  const items = transformations.filter((t) => t.beforeUrl && t.afterUrl);
  if (!items.length) return null;
  return (
    <section id="transformations" data-testid="transformations-section" className="bg-lavender-50 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          kicker="Before & After"
          title="Before the groom. After the groom."
          description="Move the divider left or right to compare each photo pair."
        />
        <div className="mt-14 grid gap-10 md:grid-cols-2" data-testid="transformations-grid">
          {items.map((t, i) => (
            <Reveal key={t.id || i} delay={0.1 * (i % 2)}>
              <BeforeAfterCard item={t} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
